import React from 'react';
import { ArrowLeft, Shield, Target, Users, BookOpen, BarChart3, Star, Trophy, Zap, Play } from 'lucide-react';
import { useApp } from './AppContext';
import shieldCheckImage320 from '../assets/shield-320w.png';
import shieldCheckImage640 from '../assets/shield-640w.png';
import shieldCheckImage966 from '../assets/shield-966w.png';
import type { Screen } from '../App';

interface AboutScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const AboutScreen: React.FC<AboutScreenProps> = ({ onNavigate, onBack }) => {
  const { getBackgroundClass } = useApp();

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Header */}
      <div className="app-header-fixed flex items-center">
        <button 
          onClick={onBack} 
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <Shield className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text truncate">
            О проекте
          </h1>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">
        
        {/* Hero Section */}
        <div className="glass-panel rounded-xl p-6 sm:p-8 md:p-10 mb-6 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl glass-panel shadow-lg flex items-center justify-center p-3">
              <img 
                src={shieldCheckImage966}
                srcSet={`${shieldCheckImage320} 320w, ${shieldCheckImage640} 640w, ${shieldCheckImage966} 966w`}
                sizes="(max-width: 640px) 80px, (max-width: 768px) 96px, 96px"
                alt="ВОХР ТЕСТ"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold glass-text mb-3">ВОХР ТЕСТ</h2>
          <p className="text-sm sm:text-base md:text-lg glass-text-secondary leading-relaxed max-w-2xl mx-auto">
            Бесплатная образовательная платформа для подготовки сотрудников ведомственной охраны к сдаче периодической проверки.
          </p>
        </div>

        {/* Mission Section */}
        <div className="glass-panel rounded-xl p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Target className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
            <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">Наша миссия</h2>
          </div>
          <p className="glass-text-secondary text-sm sm:text-base leading-relaxed">
            Мы создали ВОХР ТЕСТ, чтобы помочь сотрудникам ведомственной охраны качественно подготовиться 
            к периодической проверке и повысить уровень своих профессиональных знаний. Наша цель — сделать процесс обучения 
            удобным, доступным и эффективным для каждого пользователя.
          </p>
        </div>

        {/* Features Grid */}
        <div className="mb-6">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold glass-text mb-4 px-2">Возможности платформы</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            
            {/* Feature 1 */}
            <div className="glass-panel rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <Play className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
                <h4 className="text-sm sm:text-base font-semibold glass-text">Подготовка</h4>
              </div>
              <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                Изучайте вопросы по категориям с подробными пояснениями к ответам
              </p>
            </div>

            {/* Feature 2 */}
            <div className="glass-panel rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <Trophy className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
                <h4 className="text-sm sm:text-base font-semibold glass-text">Экзамены</h4>
              </div>
              <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                Проверяйте свои знания в условиях, максимально приближенных к реальным
              </p>
            </div>

            {/* Feature 3 */}
            <div className="glass-panel rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
                <h4 className="text-sm sm:text-base font-semibold glass-text">Марафоны</h4>
              </div>
              <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                Создавайте персональные наборы вопросов для целенаправленной подготовки
              </p>
            </div>

            {/* Feature 4 */}
            <div className="glass-panel rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
                <h4 className="text-sm sm:text-base font-semibold glass-text">Статистика</h4>
              </div>
              <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                Отслеживайте свой прогресс и анализируйте слабые места
              </p>
            </div>

            {/* Feature 5 */}
            <div className="glass-panel rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <Star className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
                <h4 className="text-sm sm:text-base font-semibold glass-text">Избранное</h4>
              </div>
              <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                Сохраняйте важные вопросы для быстрого доступа и повторения
              </p>
            </div>

            {/* Feature 6 */}
            <div className="glass-panel rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
                <h4 className="text-sm sm:text-base font-semibold glass-text">Документы</h4>
              </div>
              <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                Изучайте нормативные документы и законодательную базу
              </p>
            </div>

          </div>
        </div>

        {/* Why Choose Us */}
        <div className="glass-panel rounded-xl p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
            <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">Почему выбирают нас</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="text-sm sm:text-base font-medium glass-text mb-1">Бесплатно</h4>
                <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                  Все функции платформы доступны абсолютно бесплатно, без скрытых платежей
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="text-sm sm:text-base font-medium glass-text mb-1">Актуальные материалы</h4>
                <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                  Вопросы и документы регулярно обновляются в соответствии с действующими стандартами
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="text-sm sm:text-base font-medium glass-text mb-1">Удобный интерфейс</h4>
                <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                  Платформа работает на любых устройствах: компьютер, планшет, смартфон
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-rose-400 mt-2 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="text-sm sm:text-base font-medium glass-text mb-1">Конфиденциальность</h4>
                <p className="glass-text-secondary text-xs sm:text-sm leading-relaxed">
                  Все данные хранятся локально на вашем устройстве и не передаются третьим лицам
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="glass-panel rounded-xl p-6 sm:p-8 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 text-center">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold glass-text mb-3">Есть вопросы?</h3>
          <p className="glass-text-secondary text-sm sm:text-base mb-6 leading-relaxed">
            Мы всегда рады помочь! Свяжитесь с нами для получения поддержки или предложений по улучшению платформы.
          </p>
          <button
            onClick={() => onNavigate('contacts')}
            className="glass-interactive px-6 py-3 rounded-xl font-medium glass-text"
          >
            Связаться с нами
          </button>
        </div>

      </div>
    </div>
  );
};

